import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import type { StylingDetailRecord } from '../../types/styling-analysis.js';

/** @graph-connects none */
const TYPE_LABELS: Record<string, string> = {
  completed: '選定完了',
  rejected: '被キャンセル',
  error: 'エラー通知',
};

interface StylingDetailModalProps {
  date: string;
  type: 'completed' | 'rejected' | 'error';
  stylistName: string | null;
  details: StylingDetailRecord[];
  isLoading: boolean;
  onClose: () => void;
}

/**
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business ドリルダウンセルクリック時に個別スタイリングレコードを表示するモーダル。
 * @graph-connects none
 */
export function StylingDetailModal({
  date,
  type,
  stylistName,
  details,
  isLoading,
  onClose,
}: StylingDetailModalProps) {
  const subtitle = `${date}${stylistName ? ` / ${stylistName}` : ''}${!isLoading ? ` (${String(details.length)}件)` : ''}`;

  return (
    <Modal open onClose={onClose} size="lg" title={TYPE_LABELS[type]} description={subtitle}>
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner" />
          <span className="ml-3 text-sm text-[var(--color-text-muted)]">読み込み中...</span>
        </div>
      )}

      {!isLoading && details.length === 0 && (
        <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
          該当データがありません
        </div>
      )}

      {!isLoading && details.length > 0 && (
        <div className="space-y-3">
          {details.map((d, index) => (
            <div
              key={`${d.stylingHistoryId}-${d.rejectBy ?? ''}-${String(index)}`}
              className="rounded-lg border border-[var(--color-border)] p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1.5">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="font-mono text-xs text-[var(--color-text-muted)]">
                      #{d.stylingHistoryId}
                    </span>
                    {d.status && <StatusBadge status={d.status} />}
                    <span className="text-[var(--color-text-secondary)]">{d.stylistName}</span>
                  </div>
                  <div className="flex gap-4 text-xs text-[var(--color-text-muted)]">
                    {d.orderId && <span>注文: #{d.orderId}</span>}
                    {d.startedAt && <span>開始: {d.startedAt}</span>}
                    {d.completedAt && <span>完了: {d.completedAt}</span>}
                  </div>
                  {d.rejectReason && (
                    <div className="mt-2 rounded-md bg-[var(--color-bg-elevated)] px-3 py-2 text-xs text-[var(--color-text-secondary)]">
                      <span className="font-medium text-[var(--color-text-muted)]">
                        {d.rejectBy === 'USER' ? 'ユーザー' : 'スタイリスト'}:
                      </span>{' '}
                      {d.rejectReason}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/** @graph-connects none */
function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    STYLING_COMPLETED: {
      label: '完了',
      className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    },
    CANCELED: {
      label: 'キャンセル',
      className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    },
    ON_HOLD: {
      label: '保留中',
      className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    },
    STYLING: {
      label: '選定中',
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    },
  };

  const c = config[status] ?? {
    label: status,
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
  };

  return (
    <span className={`rounded-md px-1.5 py-0.5 text-xs font-medium ${c.className}`}>{c.label}</span>
  );
}
